import { ActivityFormType } from "PFApp/types/activity_form/activity_form_schema";
import api from "PFCore/api";
import { AdminEngagementBlueprintActivitiesAction } from "PFCore/services/admin/engagement_blueprints";
import { Collection, Id, QueryParams } from "PFCore/types";

enum ResourceType {
  Activity = "DemandEngine::Activity",
  Translation = "LLM::Translation"
}

export enum BulkOperation {
  ApplyTeamTemplate = "apply_team_template",
  Translation = "translate_with_llm",
  GeneratePDFs = "generate_cv_pdfs",
  FillAndBook = "fill_and_book"
}

type CommonOperationAttributes = {
  itemIdentifier: string;
};

type UpsertActivityActionOperation = CommonOperationAttributes & {
  action: AdminEngagementBlueprintActivitiesAction.Create | AdminEngagementBlueprintActivitiesAction.Update;
  data: ActivityFormType;
};

type DestroyActivityActionOperation = CommonOperationAttributes & {
  action: AdminEngagementBlueprintActivitiesAction.Destroy;
  data: Pick<ActivityFormType, "id">;
};

export type ActivityOperation = UpsertActivityActionOperation | DestroyActivityActionOperation;

export enum BulkOperationsState {
  Queued = "queued",
  Processing = "processing",
  PartiallyCompleted = "partially_completed",
  Completed = "completed",
  Failed = "failed"
}

export const BULK_OPERATIONS_TERMINAL_STATES: BulkOperationsState[] = [
  BulkOperationsState.Completed,
  BulkOperationsState.PartiallyCompleted,
  BulkOperationsState.Failed
];

export type BulkOperationsPayload<O extends BulkOperation, T> = {
  operation: O;
  items: T[];
};

export type BulkOperationsPusherData = {
  id: Id;
  state: BulkOperationsState;
};

export type BulkOperationsResponse = {
  id: Id;
  accountId: Id;
  creatorId: Id;
  processingStatus: {
    total: number;
    pending: number;
  };
  operation: BulkOperation;
  state: BulkOperationsState;
  createdAt: string;
  updatedAt: string;
};

export const performBulkOperations = <O extends BulkOperation, T>(
  payload: BulkOperationsPayload<O, T>
): Promise<BulkOperationsResponse> =>
  api({
    method: "POST",
    url: "bulk_operations",
    data: payload
  });

export const fetchBulkOperationsStatus = (id: Id): Promise<BulkOperationsResponse> =>
  api({
    method: "GET",
    url: `bulk_operations/${id}`
  });

export type BulkOperationsItemResult<T = any, K = string, R = unknown> = {
  id: Id;
  bulkOperationId: Id;
  position: number;
  state: BulkOperationsState;
  data: T;
  result: R | null;
  processingErrors: Record<string, string[]>;
  resourceType: ResourceType;
  resourceId: Id;
  itemIdentifier: K;
  createdAt: string;
  updatedAt: string;
};

export const fetchBulkOperationsItems = <T, K, R = unknown>(
  id: Id,
  params: Pick<QueryParams, "page" | "perPage"> = {}
): Promise<Collection<BulkOperationsItemResult<T, K, R>[]>> =>
  api({
    method: "GET",
    url: `bulk_operations/${id}/items`,
    params
  });
